"use client";

import { useState, useCallback, useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useTranslation } from "@/lib/i18n";
import { localizeHref } from "@/lib/localize-href";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cpu,
  Globe,
  Bot,
  Volume2,
  VolumeX,
  Activity,
} from "lucide-react";

/**
 * CommandCenter — A floating glassmorphism dock at the bottom of the viewport,
 * inspired by macOS Dock / Raycast. Provides quick shortcuts to:
 * - Connectivity Optimizer
 * - Network Map (3D Globe)
 * - AI Assistant panel
 * - Sound toggle
 */

interface CommandCenterProps {
  soundEnabled: boolean;
  onToggleSound: () => void;
  onPlaySound: (type: "click" | "nav" | "success") => void;
}

/* Quick‑nav items — scroll by section ID for reliability */
const DOCK_ITEMS = [
  {
    id: "configurator",
    label: "Configurator",
    labelBg: "Конфигуратор",
    icon: Cpu,
    description: "NaaS Configurator",
    sectionId: "configurator",
    href: null,
    color: "#00d4aa",
  },
  {
    id: "globe",
    label: "Network",
    labelBg: "Мрежа",
    icon: Globe,
    description: "Network Mesh",
    sectionId: "network-mesh",
    href: null,
    color: "#0088ff",
  },
  {
    id: "status",
    label: "Status (demo)",
    labelBg: "Статус (демо)",
    icon: Activity,
    description: "Status page demonstration",
    sectionId: null,
    href: "/status",
    color: "#6b7280",
  },
  {
    id: "contact",
    label: "Contact",
    labelBg: "Контакт",
    icon: Bot,
    description: "Contact Us",
    sectionId: null,
    href: "/contact",
    color: "#8b5cf6",
  },
] as const;

/* Smooth scroll to section by element ID */
function scrollToSectionById(id: string) {
  const el = document.getElementById(id);
  if (el) {
    const y = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
}

export function CommandCenter({
  soundEnabled,
  onToggleSound,
  onPlaySound,
}: CommandCenterProps) {
  const router = useRouter();
  const { language } = useTranslation();
  const pathname = usePathname() ?? "/";
  // The dock is the home page's quick navigation: two of its four items
  // scroll to #configurator and #network-mesh, which exist only on / and
  // /bg/. On every other page those buttons did nothing and the dock just
  // covered page content (QA defect 9: Next Steps on the Network Advisor).
  const normalized = pathname.replace(/\/+$/, "");
  const isHome = normalized === "" || normalized === "/bg";
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const soundLabel =
    language === "bg"
      ? soundEnabled ? "Изключи звука" : "Включи звука"
      : soundEnabled ? "Mute" : "Unmute";
  const [isVisible, setIsVisible] = useState(false);

  /* Show the dock only while the hero is on screen; it hides smoothly once
   * the visitor scrolls into the content so it never sits on top of text
   * (audit round 2, item 1). The hero is the first section in <main>. */
  useEffect(() => {
    const onScroll = () => {
      const hero = document.querySelector("#main-content > section, #main-content section");
      const heroBottom = hero ? hero.getBoundingClientRect().bottom : 400;
      setIsVisible(heroBottom > window.innerHeight * 0.5);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleItemClick = useCallback(
    (item: (typeof DOCK_ITEMS)[number]) => {
      onPlaySound("click");

      if (item.href) {
        router.push(localizeHref(item.href, language));
        return;
      }
      if (item.sectionId) {
        scrollToSectionById(item.sectionId);
      }
    },
    [onPlaySound, router, language]
  );

  return (
    <AnimatePresence>
      {isVisible && isHome && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fab-lift fixed bottom-6 left-1/2 -translate-x-1/2 z-50 hidden md:block"
        >
          {/* Dock bar */}
          <div className="relative">
            <div
              className="flex items-center gap-1 px-2 py-2 rounded-2xl"
              style={{
                background: "var(--glass-bg)",
                backdropFilter: "blur(24px)",
                WebkitBackdropFilter: "blur(24px)",
                border: "1px solid var(--card-border)",
                boxShadow: "0 8px 48px var(--shadow-color)",
              }}
            >
              {/* Dock items */}
              {DOCK_ITEMS.map((item) => {
                const Icon = item.icon;
                const isHovered = hoveredItem === item.id;
                const label = language === "bg" ? item.labelBg : item.label;

                return (
                  <motion.button
                    key={item.id}
                    onMouseEnter={() => {
                      setHoveredItem(item.id);
                      onPlaySound("nav");
                    }}
                    onMouseLeave={() => setHoveredItem(null)}
                    onClick={() => handleItemClick(item)}
                    aria-label={label}
                    title={label}
                    whileHover={{ scale: 1.15, y: -4 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl transition-colors bg-hover-bg hover:bg-hover-bg-strong"
                  >
                    <Icon
                      className="w-4 h-4 sm:w-[18px] sm:h-[18px]"
                      style={{ color: item.color }}
                    />

                    {/* Tooltip */}
                    <AnimatePresence>
                      {isHovered && (
                        <motion.div
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 4 }}
                          className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md bg-surface-light border border-border text-[10px] text-foreground font-medium whitespace-nowrap"
                        >
                          {label}
                        </motion.div>
                      )}
                    </AnimatePresence>

                  </motion.button>
                );
              })}

              {/* Separator */}
              <div className="w-px h-6 mx-1" style={{ background: "var(--divider)" }} />

              {/* Sound toggle */}
              <motion.button
                onClick={() => {
                  onToggleSound();
                  onPlaySound("click");
                }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setHoveredItem("sound")}
                onMouseLeave={() => setHoveredItem(null)}
                aria-label={soundLabel}
                aria-pressed={soundEnabled}
                title={soundLabel}
                className="relative flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl transition-colors bg-hover-bg hover:bg-hover-bg-strong"
              >
                {soundEnabled ? (
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <VolumeX className="w-4 h-4 text-muted-foreground" />
                )}

                <AnimatePresence>
                  {hoveredItem === "sound" && (
                    <motion.div
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 4 }}
                      className="absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 rounded-md bg-surface-light border border-border text-[10px] text-foreground font-medium whitespace-nowrap"
                    >
                      {soundLabel}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>

            {/* Subtle glow underneath dock */}
            <div
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-3/4 h-4 rounded-full blur-xl pointer-events-none"
              style={{
                background:
                  "radial-gradient(ellipse, rgba(0, 102, 255, 0.12) 0%, transparent 70%)",
              }}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
